import React, { useState, useEffect, useCallback } from 'react';
import { useAIQuality, useAIEmissionFactor, useAIScope3, useAIDraft, useAIConsistency, useAICrossMap, useAIHealth } from '../../hooks/ai_module_hooks';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Activity, Shield, Play, List, ChevronRight, CheckCircle, XCircle, AlertTriangle, Cpu, Zap, Layers, FileText, GitBranch, Search } from 'lucide-react';

const AGENT_META = {
  quality: { id: 'AI-01', name: 'Data Quality', icon: Shield, color: '#10b981', desc: '5-gate quality scoring', actions: ['check', 'batch_check', 'history', 'explain'] },
  ef: { id: 'AI-02', name: 'Emission Factor', icon: Search, color: '#6366f1', desc: '142K+ factor search & match', actions: ['match', 'search', 'explain', 'alternatives'] },
  scope3: { id: 'AI-03', name: 'Scope 3 Estimation', icon: Layers, color: '#f59e0b', desc: 'EEIO + Monte Carlo', actions: ['estimate', 'batch_estimate', 'uncertainty', 'breakdown'] },
  draft: { id: 'AI-07', name: 'Disclosure Drafter', icon: FileText, color: '#ec4899', desc: 'RAG-powered text generation', actions: ['generate', 'improve', 'translate', 'compare'] },
  consistency: { id: 'AI-14', name: 'Consistency Checker', icon: GitBranch, color: '#8b5cf6', desc: 'Cross-framework verification', actions: ['check', 'check_pair', 'explain', 'resolve'] },
  crossmap: { id: 'AI-05', name: 'Cross-FW Mapper', icon: Zap, color: '#06b6d4', desc: 'Datapoint-to-framework mapping', actions: ['suggest', 'confirm', 'reject', 'explain'] },
};

const SAMPLE_PAYLOADS = {
  'quality/check': { datapoint_id: 'dp1', value: 42850, unit: 'tCO2e', methodology: 'GHG Protocol', prior_year_value: 41000 },
  'quality/batch_check': { datapoint_ids: ['dp1', 'dp2', 'dp3'] },
  'quality/explain': { datapoint_id: 'dp1', value: 42850, unit: 'tCO2e' },
  'quality/history': { datapoint_id: 'dp1' },
  'ef/match': { activity: 'natural gas', geography: 'UK' },
  'ef/search': { activity: 'diesel', geography: 'UK' },
  'ef/explain': { activity: 'coal', geography: 'global' },
  'ef/alternatives': { activity: 'electricity', geography: 'US' },
  'scope3/estimate': { spend_data: [{ category: 1, spend: 100000 }, { category: 6, spend: 50000 }] },
  'scope3/batch_estimate': { spend_data: [{ category: 1, spend: 200000 }] },
  'scope3/uncertainty': { spend_data: [{ category: 1, spend: 100000 }] },
  'scope3/breakdown': { spend_data: [{ category: 1, spend: 100000 }, { category: 2, spend: 80000 }] },
  'draft/generate': { framework_id: 'GRI', section_id: '305-1', datapoint_ids: ['dp1', 'dp2'], tone: 'formal' },
  'draft/improve': { existing_draft: 'Scope 1 emissions were 42,850 tCO2e.', feedback: 'Add more context' },
  'draft/translate': { text: 'Sample disclosure text', target_language: 'fr' },
  'draft/compare': { drafts: ['Draft A', 'Draft B'] },
  'consistency/check': { disclosure_drafts: [{ framework_id: 'ESRS', content: 'Scope 1: 42,850 tCO2e' }, { framework_id: 'GRI', content: 'Direct GHG: 42,850 tCO2e' }] },
  'consistency/check_pair': { framework_a: { framework_id: 'ESRS', content: '42850' }, framework_b: { framework_id: 'GRI', content: '42900' } },
  'consistency/explain': { disclosure_drafts: [{ framework_id: 'ESRS', content: '42850' }] },
  'consistency/resolve': { discrepancy_id: 'scope1_ghg' },
  'crossmap/suggest': { datapoint_id: 'dp1' },
  'crossmap/confirm': { datapoint_id: 'dp1', framework: 'GRI', code: '305-1' },
  'crossmap/reject': { datapoint_id: 'dp1', framework: 'SASB', reason: 'Partial match' },
  'crossmap/explain': { datapoint_id: 'dp1' },
};

// ===== Status Badge =====
function StatusBadge({ status }) {
  const s = status === 'closed' ? 'online' : 'offline';
  return (
    <span data-testid={`status-badge-${s}`} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${s === 'online' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s === 'online' ? 'bg-emerald-400' : 'bg-red-400'}`} />
      {s === 'online' ? 'Online' : 'Circuit Open'}
    </span>
  );
}

// ===== Tab Navigation =====
function TabNav({ tabs, active, onChange }) {
  return (
    <div data-testid="ai-tab-nav" className="flex gap-1 p-1 bg-[#1a1a2e] rounded-lg border border-white/5">
      {tabs.map(t => (
        <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => onChange(t.id)}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${active === t.id ? 'bg-[#2a2a4a] text-white shadow-md' : 'text-gray-400 hover:text-gray-200'}`}>
          <t.icon className="inline w-4 h-4 mr-1.5 -mt-0.5" />{t.label}
        </button>
      ))}
    </div>
  );
}

// ===== Command Center Tab =====
function CommandCenter({ agents, health }) {
  return (
    <div data-testid="command-center-tab" className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-[#1a1a2e] border-emerald-500/20">
          <CardContent className="pt-5">
            <div className="flex items-center justify-between">
              <div><p className="text-sm text-gray-400">System Status</p><p data-testid="system-status" className="text-2xl font-bold text-emerald-400">{health?.status === 'healthy' ? 'Healthy' : 'Degraded'}</p></div>
              <CheckCircle className="w-8 h-8 text-emerald-500/60" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#1a1a2e] border-indigo-500/20">
          <CardContent className="pt-5">
            <div className="flex items-center justify-between">
              <div><p className="text-sm text-gray-400">Active Agents</p><p data-testid="active-agents-count" className="text-2xl font-bold text-indigo-400">{health?.healthy_agents || 0}/{health?.total_agents || 0}</p></div>
              <Cpu className="w-8 h-8 text-indigo-500/60" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#1a1a2e] border-amber-500/20">
          <CardContent className="pt-5">
            <div className="flex items-center justify-between">
              <div><p className="text-sm text-gray-400">Guardrail Layers</p><p className="text-2xl font-bold text-amber-400">5 Active</p></div>
              <Shield className="w-8 h-8 text-amber-500/60" />
            </div>
          </CardContent>
        </Card>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {Object.entries(AGENT_META).map(([key, meta]) => {
          const agentHealth = health?.agents?.[key];
          const Icon = meta.icon;
          return (
            <Card key={key} data-testid={`agent-card-${key}`} className="bg-[#1a1a2e] border-white/5 hover:border-white/15 transition-all">
              <CardContent className="pt-5 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${meta.color}20` }}>
                      <Icon className="w-5 h-5" style={{ color: meta.color }} />
                    </div>
                    <div>
                      <p className="font-medium text-white text-sm">{meta.name}</p>
                      <p className="text-xs text-gray-500">{meta.id}</p>
                    </div>
                  </div>
                  <StatusBadge status={agentHealth?.circuit_breaker?.state || 'closed'} />
                </div>
                <p className="text-xs text-gray-400">{meta.desc}</p>
                <div className="flex flex-wrap gap-1">
                  {meta.actions.map(a => (
                    <span key={a} className="px-1.5 py-0.5 text-[10px] rounded bg-white/5 text-gray-400">{a}</span>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// ===== Agent Explorer Tab =====
function AgentExplorer({ agents }) {
  const [selected, setSelected] = useState('quality');
  const meta = AGENT_META[selected];
  const Icon = meta.icon;
  return (
    <div data-testid="agent-explorer-tab" className="grid grid-cols-12 gap-4 min-h-[500px]">
      <div className="col-span-3 space-y-1">
        {Object.entries(AGENT_META).map(([key, m]) => {
          const I = m.icon;
          return (
            <button key={key} data-testid={`explorer-agent-${key}`} onClick={() => setSelected(key)}
              className={`w-full text-left px-3 py-2.5 rounded-lg flex items-center gap-2.5 transition-all text-sm ${selected === key ? 'bg-[#2a2a4a] text-white border border-white/10' : 'text-gray-400 hover:bg-white/5'}`}>
              <I className="w-4 h-4" style={{ color: m.color }} />{m.name}
              {selected === key && <ChevronRight className="w-3 h-3 ml-auto" />}
            </button>
          );
        })}
      </div>
      <div className="col-span-9 space-y-4">
        <Card className="bg-[#1a1a2e] border-white/5">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: `${meta.color}20` }}>
                <Icon className="w-6 h-6" style={{ color: meta.color }} />
              </div>
              <div>
                <CardTitle className="text-white text-lg">{meta.name}</CardTitle>
                <p className="text-sm text-gray-400">{meta.id} &middot; {meta.desc}</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-xs text-gray-500 mb-2 uppercase tracking-wider">API Contract</p>
              <code className="block bg-black/40 rounded-lg p-3 text-xs text-emerald-400 font-mono">
                POST /api/v1/ai/{selected}/{'{action}'}
              </code>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-2 uppercase tracking-wider">Available Actions</p>
              <div className="grid grid-cols-2 gap-2">
                {meta.actions.map(a => (
                  <div key={a} className="bg-black/20 rounded-lg p-3 border border-white/5">
                    <p className="text-sm font-medium text-white">{a}</p>
                    <code className="text-[10px] text-gray-500">POST /api/v1/ai/{selected}/{a}</code>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-2 uppercase tracking-wider">Serving Modules</p>
              <div className="flex flex-wrap gap-2">
                {['Voluntary FW', 'CSDDD', 'CSRD/ESRS', 'EU Taxonomy', 'Board Dashboard', 'Evidence Hub'].map(m => (
                  <span key={m} className="px-2 py-1 text-xs rounded-md bg-white/5 text-gray-300">{m}</span>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ===== Guardrails Tab =====
function GuardrailsView() {
  const layers = [
    { num: 1, name: 'Input Validation', desc: 'Schema + PII + scope check', latency: '<50ms', status: 'active', color: '#10b981' },
    { num: 2, name: 'Source Grounding', desc: 'Every claim must cite [source:dpX]', latency: '<100ms', status: 'active', color: '#6366f1' },
    { num: 3, name: 'Confidence Threshold', desc: 'Per-agent calibrated thresholds', latency: '<1ms', status: 'active', color: '#f59e0b' },
    { num: 4, name: 'Cross-Validation', desc: 'Deterministic consistency rules', latency: '<10s', status: 'active', color: '#ec4899' },
    { num: 5, name: 'Human Gate', desc: 'No auto-approve path exists', latency: 'N/A', status: 'enforced', color: '#ef4444' },
  ];
  const thresholds = [
    { agent: 'Quality', threshold: 70 }, { agent: 'EF', threshold: 80 }, { agent: 'Scope 3', threshold: 60 },
    { agent: 'Draft', threshold: 85 }, { agent: 'Consistency', threshold: 90 }, { agent: 'CrossMap', threshold: 75 },
  ];
  return (
    <div data-testid="guardrails-tab" className="space-y-6">
      <div className="space-y-3">
        {layers.map(l => (
          <div key={l.num} className="flex items-center gap-4 bg-[#1a1a2e] rounded-xl p-4 border border-white/5">
            <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm" style={{ background: `${l.color}20`, color: l.color }}>L{l.num}</div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <p className="font-medium text-white text-sm">{l.name}</p>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${l.status === 'enforced' ? 'bg-red-500/15 text-red-400' : 'bg-emerald-500/15 text-emerald-400'}`}>{l.status}</span>
              </div>
              <p className="text-xs text-gray-400">{l.desc}</p>
            </div>
            <span className="text-xs text-gray-500 font-mono">{l.latency}</span>
          </div>
        ))}
      </div>
      <Card className="bg-[#1a1a2e] border-white/5">
        <CardHeader><CardTitle className="text-white text-base">Confidence Thresholds</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {thresholds.map(t => (
            <div key={t.agent} className="flex items-center gap-3">
              <span className="text-sm text-gray-300 w-24">{t.agent}</span>
              <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden">
                <div className="h-full rounded-full bg-gradient-to-r from-amber-500 to-emerald-500 transition-all" style={{ width: `${t.threshold}%` }} />
              </div>
              <span className="text-xs text-gray-400 w-10 text-right">{t.threshold}%</span>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

// ===== Run Agent Tab =====
function RunAgent() {
  const [agent, setAgent] = useState('quality');
  const [action, setAction] = useState('check');
  const [payload, setPayload] = useState('');
  const [output, setOutput] = useState(null);
  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState(null);

  const hooks = {
    quality: useAIQuality('ai-dashboard'),
    ef: useAIEmissionFactor('ai-dashboard'),
    scope3: useAIScope3('ai-dashboard'),
    draft: useAIDraft('ai-dashboard'),
    consistency: useAIConsistency('ai-dashboard'),
    crossmap: useAICrossMap('ai-dashboard'),
  };

  useEffect(() => {
    const key = `${agent}/${action}`;
    setPayload(JSON.stringify(SAMPLE_PAYLOADS[key] || {}, null, 2));
    setOutput(null);
    setRunError(null);
  }, [agent, action]);

  const run = useCallback(async () => {
    setRunning(true);
    setRunError(null);
    setOutput(null);
    try {
      const p = JSON.parse(payload);
      const hook = hooks[agent];
      const actionFn = hook[action] || hook[action.replace('_', '')];
      if (!actionFn) throw new Error(`No hook function for ${agent}/${action}`);
      const result = await actionFn(p);
      setOutput(result);
    } catch (e) {
      setRunError(e.message);
    } finally {
      setRunning(false);
    }
  }, [agent, action, payload, hooks]);

  const meta = AGENT_META[agent];
  const confidence = output?.confidence;

  return (
    <div data-testid="run-agent-tab" className="grid grid-cols-12 gap-4">
      <div className="col-span-5 space-y-4">
        <Card className="bg-[#1a1a2e] border-white/5">
          <CardContent className="pt-5 space-y-4">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Agent</label>
              <select data-testid="run-agent-select" value={agent} onChange={e => { setAgent(e.target.value); setAction(AGENT_META[e.target.value].actions[0]); }}
                className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
                {Object.entries(AGENT_META).map(([k, m]) => <option key={k} value={k}>{m.id} {m.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Action</label>
              <select data-testid="run-action-select" value={action} onChange={e => setAction(e.target.value)}
                className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
                {meta.actions.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Payload (JSON)</label>
              <textarea data-testid="run-payload-input" value={payload} onChange={e => setPayload(e.target.value)}
                className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-white font-mono h-48 resize-none" />
            </div>
            <Button data-testid="run-execute-btn" onClick={run} disabled={running}
              className="w-full bg-indigo-600 hover:bg-indigo-500 text-white">
              <Play className="w-4 h-4 mr-2" />{running ? 'Running...' : 'Execute'}
            </Button>
          </CardContent>
        </Card>
      </div>
      <div className="col-span-7 space-y-4">
        {runError && (
          <Card className="bg-red-500/10 border-red-500/20">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-red-400">
                <XCircle className="w-4 h-4" /><span className="text-sm font-medium">Error</span>
              </div>
              <p data-testid="run-error" className="text-xs text-red-300 mt-1">{runError}</p>
            </CardContent>
          </Card>
        )}
        {output && (
          <>
            <div className="flex gap-3">
              {confidence != null && (
                <Card className="bg-[#1a1a2e] border-white/5 flex-1">
                  <CardContent className="pt-4 flex items-center justify-between">
                    <span className="text-sm text-gray-400">Confidence</span>
                    <span data-testid="run-confidence" className={`text-xl font-bold ${confidence >= 80 ? 'text-emerald-400' : confidence >= 60 ? 'text-amber-400' : 'text-red-400'}`}>{confidence}%</span>
                  </CardContent>
                </Card>
              )}
              {output.elapsed_ms != null && (
                <Card className="bg-[#1a1a2e] border-white/5 flex-1">
                  <CardContent className="pt-4 flex items-center justify-between">
                    <span className="text-sm text-gray-400">Latency</span>
                    <span className="text-xl font-bold text-gray-200">{output.elapsed_ms}ms</span>
                  </CardContent>
                </Card>
              )}
              {output.requires_human_review != null && (
                <Card className="bg-[#1a1a2e] border-white/5 flex-1">
                  <CardContent className="pt-4 flex items-center justify-between">
                    <span className="text-sm text-gray-400">Human Review</span>
                    <span className="text-sm font-medium text-amber-400">{output.requires_human_review ? 'Required' : 'N/A'}</span>
                  </CardContent>
                </Card>
              )}
            </div>
            {output.guardrail_warnings?.length > 0 && (
              <Card className="bg-amber-500/10 border-amber-500/20">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2 text-amber-400 mb-2">
                    <AlertTriangle className="w-4 h-4" /><span className="text-sm font-medium">Guardrail Warnings</span>
                  </div>
                  {output.guardrail_warnings.map((w, i) => (
                    <p key={i} className="text-xs text-amber-300">Layer {w.layer}: {w.detail}</p>
                  ))}
                </CardContent>
              </Card>
            )}
            <Card className="bg-[#1a1a2e] border-white/5">
              <CardHeader className="pb-2"><CardTitle className="text-white text-sm">Response</CardTitle></CardHeader>
              <CardContent>
                <pre data-testid="run-output" className="bg-black/40 rounded-lg p-4 text-xs text-gray-300 font-mono overflow-auto max-h-96 whitespace-pre-wrap">
                  {JSON.stringify(output, null, 2)}
                </pre>
              </CardContent>
            </Card>
          </>
        )}
        {!output && !runError && (
          <div className="flex items-center justify-center h-64 text-gray-500 text-sm">
            Select an agent and action, then click Execute
          </div>
        )}
      </div>
    </div>
  );
}

// ===== Audit Tab =====
function AuditTrail() {
  return (
    <div data-testid="audit-trail-tab" className="space-y-4">
      <Card className="bg-[#1a1a2e] border-white/5">
        <CardContent className="pt-5">
          <p className="text-sm text-gray-400">
            The AI Audit Trail captures every agent execution, guardrail check, and event emission. 
            All events are hash-chained for tamper-evidence and retained for 90-365 days depending on type.
          </p>
          <div className="mt-4 grid grid-cols-3 gap-4">
            {['ai.agent.completed', 'ai.guardrails.blocked', 'ai.quality.flagged', 'ai.draft.generated', 'ai.consistency.checked', 'ai.crossmap.confirmed'].map(et => (
              <div key={et} className="bg-black/20 rounded-lg p-3 border border-white/5">
                <code className="text-[10px] text-indigo-400">{et}</code>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ===== Main Dashboard =====
export default function AIIntelligenceDashboard() {
  const { health, agents, fetchHealth, loading: healthLoading } = useAIHealth();
  const [activeTab, setActiveTab] = useState('command');

  useEffect(() => { fetchHealth(); }, [fetchHealth]);

  const tabs = [
    { id: 'command', label: 'Command Center', icon: Activity },
    { id: 'explorer', label: 'Agent Explorer', icon: Cpu },
    { id: 'guardrails', label: 'Guardrails', icon: Shield },
    { id: 'run', label: 'Run Agent', icon: Play },
    { id: 'audit', label: 'Audit Trail', icon: List },
  ];

  return (
    <div data-testid="ai-intelligence-dashboard" className="min-h-screen bg-[#0f0f1e] text-white p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              AI Intelligence Layer
            </h1>
            <p className="text-sm text-gray-400 mt-1">Central orchestration for all AI agents across DisclosureOS</p>
          </div>
          <Button data-testid="refresh-health-btn" variant="outline" size="sm" onClick={fetchHealth} disabled={healthLoading}
            className="border-white/10 text-gray-300 hover:bg-white/5">
            <Activity className={`w-4 h-4 mr-2 ${healthLoading ? 'animate-spin' : ''}`} />Refresh
          </Button>
        </div>
        <TabNav tabs={tabs} active={activeTab} onChange={setActiveTab} />
        {activeTab === 'command' && <CommandCenter agents={agents} health={health} />}
        {activeTab === 'explorer' && <AgentExplorer agents={agents} />}
        {activeTab === 'guardrails' && <GuardrailsView />}
        {activeTab === 'run' && <RunAgent />}
        {activeTab === 'audit' && <AuditTrail />}
      </div>
    </div>
  );
}
